import type { ConfigUiHints } from "../types.ts";

export type JsonSchema = {
  type?: string | string[];
  title?: string;
  description?: string;
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema | JsonSchema[];
  additionalProperties?: JsonSchema | boolean;
  enum?: unknown[];
  const?: unknown;
  default?: unknown;
  anyOf?: JsonSchema[];
  oneOf?: JsonSchema[];
  allOf?: JsonSchema[];
  nullable?: boolean;
};

export function schemaType(schema: JsonSchema): string | undefined {
  if (!schema) {
    return undefined;
  }
  if (Array.isArray(schema.type)) {
    const filtered = schema.type.filter((t) => t !== "null");
    return filtered[0] ?? schema.type[0];
  }
  return schema.type;
}

export function defaultValue(schema?: JsonSchema): unknown {
  if (!schema) {
    return "";
  }
  if (schema.default !== undefined) {
    return schema.default;
  }
  const type = schemaType(schema);
  switch (type) {
    case "object":
      return {};
    case "array":
      return [];
    case "boolean":
      return false;
    case "number":
    case "integer":
      return 0;
    case "string":
      return "";
    default:
      return "";
  }
}

export function pathKey(path: Array<string | number>): string {
  return path.filter((segment) => typeof segment === "string").join(".");
}

export function hintForPath(path: Array<string | number>, hints: ConfigUiHints) {
  const key = pathKey(path);
  const direct = hints[key];
  if (direct) {
    return direct;
  }
  const segments = key.split(".");
  for (const [hintKey, hint] of Object.entries(hints)) {
    if (!hintKey.includes("*")) {
      continue;
    }
    const hintSegments = hintKey.split(".");
    if (hintSegments.length !== segments.length) {
      continue;
    }
    let match = true;
    for (let i = 0; i < segments.length; i += 1) {
      if (hintSegments[i] !== "*" && hintSegments[i] !== segments[i]) {
        match = false;
        break;
      }
    }
    if (match) {
      return hint;
    }
  }
  return undefined;
}

const COMMON_TRANSLATIONS: Record<string, string> = {
  enabled: "启用",
  port: "端口",
  host: "主机",
  username: "用户名",
  password: "密码",
  token: "令牌",
  timeout: "超时",
  retries: "重试次数",
  url: "URL",
  name: "名称",
  description: "描述",
  type: "类型",
  model: "模型",
  provider: "提供商",
  apiKey: "API 密钥",
  logLevel: "日志级别",
  format: "格式",
  theme: "主题",
  language: "语言",
  id: "ID",
  title: "标题",
  path: "路径",
  command: "命令",
  args: "参数",
  env: "环境变量",
  cwd: "工作目录",
  shell: "Shell",
  icon: "图标",
  color: "颜色",
  order: "顺序",
  hidden: "隐藏",
  group: "分组",
  version: "版本",
  debug: "调试",
  verbose: "详细模式",
  proxy: "代理",
  ssl: "SSL",
  tls: "TLS",
  cert: "证书",
  key: "密钥",
  ca: "CA 证书",
  headers: "请求头",
  method: "方法",
  body: "请求体",
  query: "查询参数",
  params: "路径参数",
  response: "响应",
  status: "状态",
  code: "代码",
  message: "消息",
  error: "错误",
  data: "数据",
  result: "结果",
  value: "值",
  default: "默认值",
  required: "必填",
  items: "项",
  properties: "属性",
  users: "用户",
  roles: "角色",
  permissions: "权限",
  settings: "设置",
  options: "选项",
  configs: "配置",
};

export function humanize(raw: string) {
  if (COMMON_TRANSLATIONS[raw]) {
    return COMMON_TRANSLATIONS[raw];
  }
  return raw
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .replace(/^./, (m) => m.toUpperCase());
}

export function isSensitivePath(path: Array<string | number>): boolean {
  const key = pathKey(path).toLowerCase();
  return (
    key.includes("token") ||
    key.includes("password") ||
    key.includes("secret") ||
    key.includes("apikey") ||
    key.endsWith("key")
  );
}
